/**
 * Internationalization scaffolding.
 *
 * Provides a minimal, dependency-free translation function that works today
 * and can be swapped for react-i18next without changing call sites:
 *
 *   import { t, setLocale, useLocale } from "@/lib/i18n";
 *   t("landing.hero.title");
 *
 * Locales: English (en), Spanish (es), French (fr), German (de).
 */

import { useEffect, useState } from "react";
import { migrateStorageKey } from "@/lib/migrateStorageKey";

export type Locale = "en" | "es" | "fr" | "de";

export const SUPPORTED_LOCALES: Locale[] = ["en", "es", "fr", "de"];

export const LOCALE_LABELS: Record<Locale, string> = {
  en: "English",
  es: "Español",
  fr: "Français",
  de: "Deutsch",
};

type Dictionary = Record<string, string>;

const dictionaries: Record<Locale, Dictionary> = {
  en: {
    "landing.hero.title": "Energy planning for life with MS",
    "landing.hero.subtitle": "Track patterns, predict crashes, decide what matters.",
    "landing.cta.start": "Request access",
    "landing.cta.signin": "Sign in",
    "nav.dashboard": "Dashboard",
    "nav.checkin": "Check-in",
    "nav.insights": "Insights",
    "nav.settings": "Settings",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.loading": "Loading…",
  },
  es: {
    "landing.hero.title": "Planificación de energía para vivir con EM",
    "landing.hero.subtitle": "Rastrea patrones, predice caídas, decide lo que importa.",
    "landing.cta.start": "Solicitar acceso",
    "landing.cta.signin": "Iniciar sesión",
    "nav.dashboard": "Panel",
    "nav.checkin": "Registro",
    "nav.insights": "Análisis",
    "nav.settings": "Ajustes",
    "common.save": "Guardar",
    "common.cancel": "Cancelar",
    "common.loading": "Cargando…",
  },
  fr: {
    "landing.hero.title": "Planifier son énergie en vivant avec la SEP",
    "landing.hero.subtitle": "Suivez les tendances, anticipez les chutes, choisissez l'essentiel.",
    "landing.cta.start": "Demander l'accès",
    "landing.cta.signin": "Se connecter",
    "nav.dashboard": "Tableau de bord",
    "nav.checkin": "Bilan",
    "nav.insights": "Analyses",
    "nav.settings": "Paramètres",
    "common.save": "Enregistrer",
    "common.cancel": "Annuler",
    "common.loading": "Chargement…",
  },
  de: {
    "landing.hero.title": "Energieplanung für das Leben mit MS",
    "landing.hero.subtitle": "Muster erkennen, Einbrüche vorhersagen, das Wichtige entscheiden.",
    "landing.cta.start": "Zugang anfragen",
    "landing.cta.signin": "Anmelden",
    "nav.dashboard": "Übersicht",
    "nav.checkin": "Check-in",
    "nav.insights": "Einblicke",
    "nav.settings": "Einstellungen",
    "common.save": "Speichern",
    "common.cancel": "Abbrechen",
    "common.loading": "Lädt…",
  },
};

const LOCALE_KEY = "myelina_locale";
const LEGACY_LOCALE_KEYS = ["bsofms_locale"];
const listeners = new Set<(l: Locale) => void>();

function detectInitial(): Locale {
  if (typeof window === "undefined") return "en";
  const stored = migrateStorageKey(
    window.localStorage,
    LOCALE_KEY,
    LEGACY_LOCALE_KEYS,
  ) as Locale | null;
  if (stored && SUPPORTED_LOCALES.includes(stored)) return stored;
  const nav = window.navigator?.language?.slice(0, 2).toLowerCase();
  return SUPPORTED_LOCALES.includes(nav as Locale) ? (nav as Locale) : "en";
}

let currentLocale: Locale = detectInitial();

export function getLocale(): Locale {
  return currentLocale;
}

export function setLocale(locale: Locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) return;
  currentLocale = locale;
  if (typeof window !== "undefined") {
    window.localStorage?.setItem(LOCALE_KEY, locale);
    document.documentElement.setAttribute("lang", locale);
  }
  listeners.forEach((cb) => cb(locale));
}

export function t(key: string, fallback?: string): string {
  return dictionaries[currentLocale]?.[key] ?? dictionaries.en[key] ?? fallback ?? key;
}

export function useLocale() {
  const [locale, setLocaleState] = useState<Locale>(currentLocale);
  useEffect(() => {
    const cb = (l: Locale) => setLocaleState(l);
    listeners.add(cb);
    return () => {
      listeners.delete(cb);
    };
  }, []);
  return { locale, setLocale, t };
}
